/** @jsxImportSource react */
import { Box, Button, Link, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { recordsAPI } from '../../api/crm'
import { usePublishedForm, useTenantKey } from '../../hooks/useCrmQueries'
import { useEntityAccess } from '../../hooks/usePermissions'
import { allFields, formatValue } from './fieldValues'
import { LookupLink } from './LookupInput'
import LineItemsTable from './LineItemsTable'
import { entityLabel, entityLabelPlural, formatRelativeTime } from '../../utils/helpers'

const KEY_COLUMNS = 3

/** Title of a related group: "Line items", "Sub-accounts", "Contacts · Account". */
const groupTitle = (g, record) => {
  if (g.parentLink && g.entityType === record.entityType) return `Sub-${entityLabelPlural(g.entityType).toLowerCase()}`
  return entityLabelPlural(g.entityType)
}

/** A related group as a list: title plus a few key columns from the child's form. */
const RelatedList = ({ g, record, form }) => {
  const navigate = useNavigate()
  const access = useEntityAccess(g.entityType)
  const fields = form ? allFields(form) : []
  const titleKey = fields.find((f) => f.fieldType === 'TEXT')?.fieldKey
  const columns = fields
    .filter((f) => f.fieldKey !== g.fieldKey && f.fieldKey !== titleKey && !f.hidden && !['TEXTAREA', 'MULTI_SELECT'].includes(f.fieldType))
    .slice(0, KEY_COLUMNS)
  const newUrl = `/records/${g.entityType}/new?form=${encodeURIComponent(g.formCode)}&prefill.${g.fieldKey}=${record.id}`
  const listUrl = `/records/${g.entityType}?filter.${g.fieldKey}=${record.id}`
  const sameType = g.entityType === record.entityType

  return (
    <Paper variant="outlined">
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.25, gap: 1 }}>
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          {groupTitle(g, record)}
          <Typography component="span" variant="body2" color="text.secondary">{g.parentLink ? '' : ` · ${g.fieldLabel}`} · {g.total}</Typography>
        </Typography>
        {access.canCreate && (
          <Button startIcon={<AddIcon />} onClick={() => navigate(newUrl)}>
            New {sameType ? `sub-${entityLabel(g.entityType).toLowerCase()}` : entityLabel(g.entityType).toLowerCase()}
          </Button>
        )}
      </Box>
      {g.items.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            {columns.length > 0 && (
              <TableHead>
                <TableRow>
                  <TableCell>{fields.find((f) => f.fieldKey === titleKey)?.label || 'Name'}</TableCell>
                  {columns.map((c) => <TableCell key={c.fieldKey}>{c.label}</TableCell>)}
                  <TableCell align="right">Updated</TableCell>
                </TableRow>
              </TableHead>
            )}
            <TableBody>
              {g.items.map((r) => (
                <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/records/${r.entityType}/${r.id}`)}>
                  <TableCell sx={{ fontWeight: 500 }}>{r.title}</TableCell>
                  {columns.map((c) => (
                    <TableCell key={c.fieldKey}>
                      {c.fieldType === 'LOOKUP' || c.fieldType === 'USER' ? <LookupLink refValue={r.lookups?.[c.fieldKey]} /> : formatValue(c, r.data[c.fieldKey])}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{formatRelativeTime(r.updatedOn)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {g.total > g.items.length && (
        <Box sx={{ px: 2, py: 1 }}>
          <Link component={RouterLink} to={listUrl} variant="body2">View all {g.total}</Link>
        </Box>
      )}
    </Paper>
  )
}

const RelatedGroup = ({ g, record }) => {
  const { data: form } = usePublishedForm(g.formCode)
  if (g.parentLink?.display === 'TABLE' && g.entityType !== record.entityType) {
    return form ? <LineItemsTable group={g} parent={record} childForm={form} /> : null
  }
  return <RelatedList g={g} record={record} form={form} />
}

/**
 * Records whose LOOKUP fields point here: line items (editable table), sub-records of a tree, and
 * other linked records ("Contacts at this Account") with a few key columns. Line items come first.
 */
const RelatedRecords = ({ record }) => {
  const tenantId = useTenantKey()
  const { data: groups = [] } = useQuery({
    queryKey: [tenantId, 'records', 'related', record.id],
    queryFn: () => recordsAPI.related(record.id),
  })
  if (groups.length === 0) return null
  const rank = (g) => (g.parentLink?.display === 'TABLE' && g.entityType !== record.entityType ? 0 : g.parentLink ? 1 : 2)
  const sorted = [...groups].sort((a, b) => rank(a) - rank(b))

  return (
    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6">Related</Typography>
      {sorted.map((g) => <RelatedGroup key={`${g.entityType}.${g.fieldKey}`} g={g} record={record} />)}
    </Box>
  )
}

export default RelatedRecords
